import { supabase, tagged } from '../lib/supabase';
import { isDemoMode } from '../lib/demo';
import { base64ToArrayBuffer } from '../lib/images';
import { normalizeUrl } from '../lib/url';
import { reportError } from '../lib/report';
import { stillOnFilter } from '../lib/discoverWhen';
import { freshChannel } from '../lib/realtimeChannel';
import { parseAlternatives } from '../lib/fieldAlternatives';
import { richDescription } from '../lib/richText';
import { primaryImageUrl } from '../lib/eventImage';
import type { MergePick } from '../lib/tagCounts';
import { demoStore } from '../demo/store';
import type {
  AppNotification, AvailabilityCell, Business, DiscoverIndexItem, Dow, EventEditDraft, EventItem,
  EventOccurrence, EventVersion, HashEntry, NotifCategory, RankedSlot, SuggestionDraft, Tag, UserId,
} from '../domain/types';

/* eslint-disable @typescript-eslint/no-explicit-any */

/**
 * اعلان‌هایی که تا کاری براشون نکنی نباید پاک شن (مایگریشن 0010).
 * `friend_accepted` عمداً این‌جا نیست — خبره، کاری ازت نمی‌خواد.
 *
 * `friend_declined` (مایگریشن 0075) استثناست و عمداً این‌جاست: خبره، ولی
 * تنها جاییه که نوشته شده. ردیفِ دوستیِ ردشده از `friendRepo.list` بیرونه،
 * پس اگه با یه نگاه به صفحه پاک شه، کاربر هیچ‌وقت نمی‌فهمه چرا اون اسم از
 * فهرستش رفت. با زدنِ «باشه» خونده می‌شه.
 */
export const ACTIONABLE_KINDS = ['signal_received', 'friend_request', 'friend_declined'];
export const isActionable = (kind: string) => ACTIONABLE_KINDS.includes(kind);

/** خبرهایی که به آدم‌ها ربط دارن — یعنی نقطه‌شون روی تبِ دوستان می‌شینه. */
export const FRIEND_KINDS = [
  'friend_request', 'friend_accepted', 'friend_declined', 'contact_joined',
];

/**
 * آینه‌ی تابع `notification_category` توی مایگریشن 0008 (و 0075) —
 * با یک واگراییِ عمدی: SQL برای `tag_event` دسته‌ی `tag_follow` رو
 * برمی‌گردونه (مایگریشن 0094) تا تریگرِ پوش مُد رو از ستونِ درست بخونه،
 * ولی این‌جا فقط سطلِ نقطه‌های داخلِ اپ تعیین می‌شه و tag_event توی
 * سطلِ event می‌شینه — دسته‌ی چهارمی روی تب‌ها وجود نداره که نقطه بگیره.
 */
export const categoryOfKind = (kind: string): NotifCategory => {
  if (kind === 'friend_request' || kind === 'friend_accepted'
    || kind === 'friend_declined' || kind === 'contact_joined') return 'friend_request';
  if (kind === 'signal_received' || kind === 'signal_changed'
    || kind === 'signal_cancelled' || kind === 'signal_confirmed'
    || kind === 'signal_completed' || kind === 'signal_reminder'
    || kind === 'signal_photos' || kind === 'signal_message'
    || kind === 'signal_photo_added' || kind === 'content_removed') return 'signal';
  return 'event';
};

/**
 * خبرهایی که به **داخلِ یک برنامه‌ی مشخص** اشاره می‌کنند: پیامِ تازه‌ی گفتگو
 * و عکسِ تازه (مایگریشن 0148).
 *
 * دیتابیس تا وقتی یکی از این‌ها نخوانده باشد، دومی را نمی‌سازد — «در این
 * گفتگو چیزی هست که ندیده‌ای» یک بار گفته می‌شود نه ده بار. پس خوانده کردنش
 * نیمه‌ی دومِ همان قاعده است و بدونش پیامِ بعدی هرگز خبر نمی‌دهد؛ کارِ
 * `markSignalTabSeen` پایین.
 */
export const SIGNAL_TAB_KIND: Record<'chat' | 'photos', string> = {
  chat: 'signal_message',
  photos: 'signal_photo_added',
};

/** همان دو نوع، به‌شکلی که `markSeen` و دموْ بتوانند کنارشان بگذارند. */
export const TAB_SEEN_KINDS = Object.values(SIGNAL_TAB_KIND);

/**
 * «الارم» — خبری که نباید از دست بره (مایگریشن 0072).
 *
 * فقط برای کسی ساخته می‌شه که گفته «هستم» و برنامه‌ش عوض یا لغو شده. دو تا
 * فرق با یه اعلانِ معمولی داره: پوشش از تنظیمِ کاربر رد می‌شه (کارِ تریگرِ
 * دیتابیس)، و صرفِ باز کردنِ یه صفحه «دیده شد» حسابش نمی‌کنه — نگاه کن به
 * `markSeen` پایین.
 */
export const isAlarm = (n: AppNotification): boolean => n.data?.alarm === true;

/**
 * تنظیم‌های سراسریِ اپ (مایگریشن 0088).
 *
 * فقط خواندن؛ نوشتنش کارِ `adminRepo` است چون فقط ادمین می‌تونه.
 */
export const settingsRepo = {
  /**
   * رأیِ هر رخداد به کدام تگش برسه — نگاه کن به `MergePick` در
   * core/lib/tagCounts.
   *
   * ردیفی که نباشه یا مقدارِ ناشناخته‌ای داشته باشه یعنی `most`، همون
   * رفتاری که تا امروز بوده. صفحه‌ی کشف نباید سرِ یه تنظیمِ خراب خالی بمونه.
   */
  async tagMergePick(): Promise<MergePick> {
    if (isDemoMode) return 'most';
    const { data, error } = await supabase.from('app_settings')
      .select('value').eq('key', 'tag_merge_pick').maybeSingle();
    if (error) throw error;
    return data?.value === 'least' ? 'least' : 'most';
  },
};

/**
 * ردیف‌های `tag_labels` (مایگریشن 0144) به یک شیءِ «زبان ⟵ برچسب».
 *
 * PostgREST با embed هر تگ فهرستِ برچسب‌هایش را کنارِ خودش می‌دهد، پس یک
 * رفت‌وبرگشت است نه دو تا. تگی که هنوز ردیفی ندارد `{}` می‌گیرد و
 * `tagText` به ستون‌های قدیمی می‌افتد.
 */
const mapLabels = (rows: any): Record<string, string> =>
  Object.fromEntries((rows ?? []).map((r: any) => [r.locale, r.label]));

const mapTag = (t: any): Tag => ({
  id: t.id, slug: t.slug, emoji: t.emoji, labelFa: t.label_fa, labelEn: t.label_en,
  labels: mapLabels(t.tag_labels),
  parentId: t.parent_id ?? null, archivedAt: t.archived_at ?? null,
});

/** ستون‌های تگ + برچسبِ هر زبانش. یک رشته، چون هر سه خواننده همینش را می‌خواهند. */
const TAG_COLS = '*, tag_labels(locale, label)';

export const tagRepo = {
  /**
   * همه‌ی تگ‌های زنده — یک فهرست برای همه‌جا: فرم‌ها، کشف، صفحه‌ی جزئیات.
   *
   * قبلاً دو فهرست بود (فقط activity برای انتخاب، همه با ژانرها برای کشف).
   * خواسته‌ی ۲۰۲۶-۰۸ یکی‌شون کرد: برای کاربر «دسته» و «ژانر» فرقی ندارن،
   * همه تگ‌ان و از بین همه می‌شه انتخاب کرد. `kind` توی دیتابیس می‌مونه —
   * قراردادِ کرالره (فقط ردیف‌های genre رو می‌نویسه/پاک می‌کنه، مایگریشن
   * 0064) و به چشمِ کاربر نمی‌رسه.
   */
  async list(): Promise<Tag[]> {
    if (isDemoMode) return demoStore.tags.list();
    const { data, error } = await supabase.from('tags').select(TAG_COLS).is('archived_at', null);
    if (error) throw error;
    return (data ?? []).map(mapTag);
  },

  /**
   * همه‌چیز، کنارگذاشته‌ها هم توش — فقط سربرگِ تگ‌های ادمین.
   *
   * عمداً یه تابعِ جداست و نه یه پارامترِ اختیاری روی `list`: این دو تا
   * مستقیم به `useQuery` داده می‌شن و react-query خودش یه آرگومان بهشون
   * پاس می‌ده، پس پارامترِ اختیاری یعنی «همیشه روشن».
   */
  async listWithArchived(): Promise<Tag[]> {
    if (isDemoMode) return demoStore.tags.list();
    const { data, error } = await supabase.from('tags').select(TAG_COLS);
    if (error) throw error;
    return (data ?? []).map(mapTag);
  },

  /**
   * تگِ تازه. برچسبِ هر دو زبان یکی گذاشته میشه چون کاربر یه اسم داده و
   * حدس زدنِ ترجمه‌ش بدتر از نگه داشتنِ خودِ کلمه‌ست.
   *
   * ردیف‌های `tag_labels` رو تریگرِ `tags_seed_labels` (مایگریشن 0144) خودش
   * می‌سازه — این‌جا فقط همون رو بازتاب می‌دیم تا شیءِ برگشتی با چیزی که
   * دفعه‌ی بعد از دیتابیس می‌آد یکی باشه. کاربرِ آلمانی‌زبان هم همین کلمه
   * رو می‌بینه، چون ترجمه‌ی خودکارِ اسمی که یه نفر تایپ کرده از خودِ کلمه
   * بدتره.
   *
   * `kind` همیشه activity: تنها kindیه که پالیسیِ t_ins (مایگریشن 0017) به
   * کاربر اجازه می‌ده — برای اپ فقط یه جزئیاتِ ذخیره‌سازیه، نه یه مفهوم.
   */
  async create(name: string, ownerId: UserId): Promise<Tag> {
    if (isDemoMode) return demoStore.tags.create(name);
    const label = name.trim();
    // slug باید یکتا و ماشین‌خوان باشه؛ حروف غیرلاتین با کدِ کوتاه جایگزین میشن
    // تا اسم فارسی هم slug معتبر بگیره.
    const base = label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    const slug = base || `tag-${Date.now().toString(36)}`;

    const { data, error } = await supabase.from('tags')
      .insert({ slug, emoji: '', label_fa: label, label_en: label, kind: 'activity', created_by: ownerId })
      .select('*').single();
    if (error) throw error;
    return {
      id: data.id, slug: data.slug, emoji: data.emoji,
      labelFa: data.label_fa, labelEn: data.label_en,
      labels: { en: data.label_en, fa: data.label_fa },
    };
  },
};

/**
 * نشان‌کردنِ رخداد یا بیزینس.
 *
 * جدولش کلیدِ مرکبِ (کاربر، نوع، شناسه) داره، پس «نشان کردن» یه insert است و
 * «برداشتن» یه delete — بدون هیچ حالتِ میانی و بدون امکانِ ردیفِ تکراری.
 */
export const favoriteRepo = {
  async list(): Promise<{ kind: 'event' | 'business'; refId: string }[]> {
    if (isDemoMode) return demoStore.favorites.list();
    const { data, error } = await supabase.from('favorites').select('kind, ref_id');
    if (error) throw error;
    // ref_id توی دیتابیس bigint است و به‌صورت رشته برمی‌گرده — همون‌طور که
    // شناسه‌ی خودِ رخداد و بیزینس هم توی مدل رشته‌ست.
    return (data ?? []).map((f: any) => ({ kind: f.kind, refId: String(f.ref_id) }));
  },

  async set(kind: 'event' | 'business', refId: string, on: boolean, uid: UserId): Promise<void> {
    if (isDemoMode) return demoStore.favorites.set(kind, refId, on);
    if (on) {
      const { error } = await supabase.from('favorites')
        .insert({ user_id: uid, kind, ref_id: refId });
      // ردیفِ تکراری یعنی از قبل نشان شده — خطا نیست.
      if (error && error.code !== '23505') throw error;
      return;
    }
    const { error } = await supabase.from('favorites')
      .delete().eq('kind', kind).eq('ref_id', refId).eq('user_id', uid);
    if (error) throw error;
  },
};

/**
 * دنبال کردنِ تگ (مایگریشن 0094) — همون مدلِ favorites: دنبال کردن یه
 * insert است و برداشتن یه delete، بدونِ حالتِ میانی. خودِ خبر دادن کارِ
 * دیتابیسه (tag_follow_fanout)؛ اپ فقط این فهرست رو نگه می‌داره.
 */
export const tagFollowRepo = {
  async list(): Promise<number[]> {
    if (isDemoMode) return demoStore.tagFollows.list();
    const { data, error } = await supabase.from('tag_follows').select('tag_id');
    if (error) throw error;
    return (data ?? []).map((r: any) => Number(r.tag_id));
  },

  async set(tagId: number, on: boolean, uid: UserId): Promise<void> {
    if (isDemoMode) return demoStore.tagFollows.set(tagId, on);
    if (on) {
      const { error } = await supabase.from('tag_follows')
        .insert({ user_id: uid, tag_id: tagId });
      // ردیفِ تکراری یعنی از قبل دنبال می‌شه — خطا نیست.
      if (error && error.code !== '23505') throw error;
      return;
    }
    const { error } = await supabase.from('tag_follows')
      .delete().eq('tag_id', tagId).eq('user_id', uid);
    if (error) throw error;
  },
};

/**
 * ستون‌های شهر، هر جا که یه ردیفِ کاملِ رخداد/بیزینس خونده می‌شه.
 *
 * از مایگریشن 0063 شهر یه کلیدِ خارجیه، نه رشته — ولی کارت‌ها هنوز **اسم**ِ
 * شهر رو می‌خوان (نگاه کن به cardAddress). پس همون یه join، یه جا نوشته
 * شده تا هفت تا `select` از هم جدا نیفتن.
 */
const CITY_JOIN = 'city_ref:cities(name)';

/**
 * باکتِ عکسِ رخداد (مایگریشن 0077). عمومیه، پس آدرسش بدونِ رفت‌وبرگشت به
 * سرور ساخته می‌شه — همون قاعده‌ی باکتِ `avatars`.
 */
const EVENT_IMAGE_BUCKET = 'event-images';

/**
 * سقفِ یه صفحه وقتی صداکننده خودش چیزی نخواسته.
 *
 * ۵۰ بود، و بی‌صدا می‌بُرید. اتاوا ۷۵۰ رخداد داره و کوئری `starts_at` صعودی
 * مرتب می‌شه، پس اون ۵۰ ردیف همیشه **قدیمی‌ترین**‌ها بودن — نمایشگاه‌ها و
 * جاذبه‌های چندماهه‌ای که ماه‌ها پیش شروع شدن و هنوز بازن — و هیچ‌کدوم از
 * رخدادهای همین هفته اصلاً دانلود نمی‌شدن. تا وقتی صفحه‌بندیِ واقعی نیامده،
 * پیش‌فرض «همه‌ش» است.
 *
 * ۱۰۰۰ خودِ سقفِ PostgREST است (`max-rows`): بالاتر از این، سرور خودش می‌بُره
 * و هیچ خطایی هم نمی‌ده — برای همین `warnIfFull`.
 */
const PAGE_MAX = 1000;

/**
 * صفحه‌ای که دقیقاً پُر برگشته، احتمالاً بریده شده.
 *
 * همون باگ سه ماه بی‌صدا موند چون کوئریِ بریده خطا نمی‌ده، فقط کمتر
 * برمی‌گردونه. صداکننده‌ای که خودش `limit` داده این حالت رو انتظار داره؛ اونی
 * که نداده، نه — پس فقط برای همون یکی هشدار می‌ره.
 */
function warnIfFull(where: string, got: number, limit: number, asked: number | undefined): void {
  if (asked === undefined && got >= limit) {
    reportError(where, `صفحه پُر برگشت (${got}/${limit}) — احتمالاً بریده شده`);
  }
}

/**
 * چند تا شناسه، دسته‌دسته.
 *
 * `in.(...)` کلِ فهرست رو توی خودِ **آدرسِ** درخواست می‌ذاره. ۷۵۰ تا UUID یعنی
 * یه URL حدوداً ۳۰ کیلوبایتی که سرور با ۴۱۴ ردش می‌کنه — و صداکننده‌ی اصلیِ
 * این تابع کشِ آفلاینِ گوشیه که دقیقاً همین‌قدر شناسه می‌ده. شکستش هم بی‌صدا
 * بود: `useOfflineList` خطا رو می‌گرفت و فهرستِ شهرِ **قبلی** رو نگه می‌داشت.
 */
const ID_CHUNK = 100;

async function inChunks<T>(
  ids: string[], fetchBatch: (batch: string[]) => Promise<T[]>,
): Promise<T[]> {
  if (ids.length <= ID_CHUNK) return fetchBatch(ids);
  const batches: string[][] = [];
  for (let i = 0; i < ids.length; i += ID_CHUNK) batches.push(ids.slice(i, i + ID_CHUNK));
  return (await Promise.all(batches.map(fetchBatch))).flat();
}

/** export شده چون adminRepo هم همین نگاشت رو لازم داره، با چند ستونِ اضافه روش. */
export const mapEvent = (e: any): EventItem => ({
  id: e.id, shortId: e.short_id ?? undefined,
  title: e.title, description: e.description, categoryId: e.category_id,
  tagIds: (e.event_tags ?? []).map((t: any) => t.tag_id),
  // بیزینس venue نداره، مستقیم lat/lng/address خودش رو داره (مایگریشن 0042).
  venueName: e.venue?.name, lat: e.venue?.lat ?? e.lat, lng: e.venue?.lng ?? e.lng,
  startsAt: e.starts_at, endsAt: e.ends_at, isFree: e.is_free, priceNote: e.price_note,
  occurrenceStartsAt: (e.event_occurrences ?? [])
    .map((o: any) => o.starts_at)
    .filter((startsAt: unknown): startsAt is string => typeof startsAt === 'string'),
  isPromoted: e.is_promoted, url: e.url, ticketUrl: e.ticket_url ?? null,
  // عکسی که ادمین «غیر معتبر» علامت زده نشان داده نمی‌شود، ولی آدرسش سرِ
  // جای خودش در دیتابیس مانده و هر وقت دوباره معتبر شد برمی‌گردد
  // (مایگریشن 0135). پرچم روی همین ردیف می‌آید، پس درخواستِ دومی لازم نیست.
  imageUrl: primaryImageUrl(e), weekdays: e.weekdays ?? null,
  monthdays: e.monthdays ?? null, monthWeeks: e.month_weeks ?? null,
  contentHash: e.content_hash ?? null, createdAt: e.created_at ?? null,
  kind: e.kind, hasDiscount: e.has_discount, discountNote: e.discount_note, address: e.address,
  city: e.city_ref?.name ?? null,
  // bigint از PostgREST رشته برمی‌گرده، نه عدد — همون تله‌ای که favoriteRepo
  // با ref_id خورده بود. بدونِ این، `cityId === city.id` هیچ‌وقت درست نیست.
  cityId: e.city_id == null ? null : Number(e.city_id),
  rootId: e.root_id ?? e.id, version: e.version, status: e.status,
  submittedBy: e.submitted_by ?? null, isOrganizer: e.is_organizer ?? false,
  editNote: e.edit_note, editedBy: e.edited_by,
  reviewedBy: e.reviewed_by, reviewedAt: e.reviewed_at, reviewNote: e.review_note,
  // فیلدهایی که ادمین قفلشان کرده (مایگریشن 0096). فقط فرمِ ویرایش و
  // ابزارهای ادمین می‌خوانندش؛ برای بقیه بی‌ضرر است.
  lockedFields: e.locked_fields ?? null,
  // «منبعِ دیگری چیزِ دیگری می‌گوید» — داخلِ metadataی خودِ کرالر می‌نشیند،
  // پس هیچ ستونِ تازه‌ای ندارد. کوئری‌هایی که `metadata` نمی‌گیرن (فهرستِ
  // سبکِ کشف) این‌جا `null` می‌گیرن و صفحه‌ی جزئیات که `*` می‌گیره، پرش.
  alternatives: parseAlternatives(e.metadata),
  // نسخه‌ی قالب‌دارِ توضیح — مثلِ بالایی داخلِ همان `metadata` نشسته، پس
  // کوئریِ سبکِ کشف که `metadata` نمی‌گیره این‌جا `null` می‌گیره و صفحه‌ی
  // جزئیات که `*` می‌گیره، پرش (WHOSUP-233، نگاه کن به lib/richText.ts).
  descriptionRich: richDescription(e.metadata),
});

/**
 * فهرستِ سبکِ کلِ شهر — پایه‌ی عددِ کنارِ دسته‌ها.
 *
 * یک درخواست، بدونِ صفحه‌بندی، و فقط ستون‌هایی که شمارش و فیلترهای
 * غیرمتنی لازم دارن (نگاه کن به DiscoverIndexItem). رخداد و مکان با هم
 * می‌آن، چون عددِ کنارِ دسته هر دو رو می‌شماره.
 *
 * فیلترِ `stillOnFilter` مکان‌ها رو نمی‌ندازه بیرون: شاخه‌ی
 * «starts_at و ends_at هر دو خالی» دقیقاً همون‌هان.
 */
export const discoverIndexRepo = {
  async list(cityId: number): Promise<DiscoverIndexItem[]> {
    if (isDemoMode) {
      return [
        ...demoStore.events.upcoming().map((e) => mapIndex({
          id: e.id, kind: 'event', category_id: e.categoryId,
          event_tags: (e.tagIds ?? []).map((tag_id) => ({ tag_id })),
          starts_at: e.startsAt, ends_at: e.endsAt, is_free: e.isFree,
        })),
        ...demoStore.businesses.list().map((b) => mapIndex({
          id: b.id, kind: 'business', category_id: b.categoryId,
          event_tags: (b.tagIds ?? []).map((tag_id) => ({ tag_id })),
          has_discount: b.hasDiscount,
        })),
      ];
    }
    const { data, error } = await tagged('discoverIndexRepo.list', supabase
      .from('events')
      .select('id, kind, category_id, starts_at, ends_at, weekdays, monthdays, month_weeks,'
        + ' is_free, has_discount, event_tags(tag_id)')
      .eq('city_id', cityId).eq('status', 'published')
      .or(stillOnFilter())
      // ⚠ این‌جا `.limit(5000)` بود و آن عدد یک وعده‌ی توخالی بود: سقفِ خودِ
      // PostgREST هزار است و هرچه بالاتر از آن نوشته شود، سرور بی‌صدا سرِ
      // همان هزار می‌بُرَد (نگاه کن به PAGE_MAX). یعنی سقفِ واقعی هزار بود و
      // کسی خبردار نمی‌شد. حالا سقف همان چیزی است که واقعاً هست، و اگر یک
      // روز شهری به آن برسد `warnIfFull` می‌گوید.
      .limit(PAGE_MAX));
    if (error) throw error;
    warnIfFull('discoverIndexRepo.list', data?.length ?? 0, PAGE_MAX, undefined);
    return (data ?? []).map(mapIndex);
  },
};

const mapIndex = (e: any): DiscoverIndexItem => ({
  id: e.id,
  kind: e.kind === 'business' ? 'business' : 'event',
  tagIds: (e.event_tags ?? []).map((t: any) => t.tag_id),
  categoryId: e.category_id ?? null,
  startsAt: e.starts_at ?? null,
  endsAt: e.ends_at ?? null,
  weekdays: e.weekdays ?? null,
  monthdays: e.monthdays ?? null,
  monthWeeks: e.month_weeks ?? null,
  isFree: !!e.is_free,
  hasDiscount: !!e.has_discount,
});

export const eventRepo = {
  /**
   * `cityId` پیش‌فرض نداره و باید داده شه.
   *
   * قبلاً `city = 'ottawa'` بود و همین باعث شد سه تا صداکننده اصلاً شهر
   * ندن و کسی متوجه نشه (بیزینس‌ها که اصلاً فیلتر نمی‌شدن). حالا هر
   * صداکننده‌ای که شهر نده، همون‌جا کامپایل نمی‌شه.
   */
  async upcoming(cityId: number, opts: { limit?: number; offset?: number } = {}): Promise<EventItem[]> {
    if (isDemoMode) return demoStore.events.upcoming();
    const { limit = PAGE_MAX, offset = 0 } = opts;
    const { data, error } = await tagged('eventRepo.upcoming', supabase
      .from('events')
      .select(`*, venue:venues(name,lat,lng), event_tags(tag_id), event_occurrences!event_occurrences_event_id_fkey(starts_at), ${CITY_JOIN}`)
      .eq('kind', 'event').eq('city_id', cityId).eq('status', 'published')
      .or(stillOnFilter())
      .order('is_promoted', { ascending: false })
      .order('starts_at', { ascending: true })
      .range(offset, offset + limit - 1));
    if (error) throw error;
    warnIfFull('eventRepo.upcoming', data?.length ?? 0, limit, opts.limit);
    return (data ?? []).map(mapEvent);
  },

  /**
   * شمارشِ واقعیِ رخدادهای منتشرشده - از خودِ دیتابیس، نه طولِ چیزی که
   * لود شده. عددِ کنارِ سربرگِ «رخدادها» از همین میاد، نه از shownEvents.length
   * (که با صفحه‌بندی همیشه ناقصه).
   */
  async upcomingCount(cityId: number): Promise<number> {
    if (isDemoMode) return demoStore.events.upcoming().length;
    const { count, error } = await tagged('eventRepo.upcomingCount', supabase
      .from('events').select('id', { count: 'exact', head: true })
      .eq('kind', 'event').eq('city_id', cityId).eq('status', 'published')
      .or(stillOnFilter()));
    if (error) throw error;
    return count ?? 0;
  },

  /**
   * فقط شناسه+هش، برای هر ۵۰ تا رکوردِ کامل - این چیزیه که کشِ رویِ گوشی
   * (src/core/lib/offlineCache.ts) در ابتدای هر بار باز شدنِ اپ می‌گیره
   * تا بفهمه کدوم رخداد عوض شده، بدونِ کشیدنِ همه‌چیز.
   *
   * ⚠ **`order` این‌جا تزئینی نیست، صفحه‌بندیِ گوشی روش سواره.**
   * `offlineCache.sync` ترتیبِ همین فهرست رو نگه می‌داره و «سی تای اول» رو
   * می‌گیره؛ پس اگه ترتیبی نباشه، پستگرس هر بار هر ترتیبی که خواست می‌ده و
   * اون سی‌تا سی تای **تصادفی** از کلِ شهره. اون‌وقت هر صفحه‌ای که با اسکرول
   * می‌آد، ردیف‌هاش پخش می‌شن وسطِ فهرستِ مرتب‌شده — از جمله بالای همون‌جایی
   * که کاربر داره نگاه می‌کنه — و فهرست زیرِ دستش می‌پره. همون ترتیبِ
   * `upcoming` است تا «سی تای اول» یعنی همون سی تایی که واقعاً اول دیده می‌شه.
   */
  async hashList(cityId: number): Promise<HashEntry[]> {
    if (isDemoMode) {
      return demoStore.events.upcoming().map((e) => ({ id: e.id, hash: e.contentHash ?? e.id }));
    }
    const { data, error } = await tagged('eventRepo.hashList', supabase
      .from('events').select('id, content_hash, image_invalid')
      .eq('kind', 'event').eq('city_id', cityId).eq('status', 'published')
      .or(stillOnFilter())
      .order('is_promoted', { ascending: false })
      .order('starts_at', { ascending: true })
      // ⚠ سقف صریح، وگرنه سقفِ نامرئیِ هزارِ PostgREST بی‌صدا می‌بُرَد و کشِ
      // گوشی هیچ‌وقت نمی‌فهمد که نصفِ شهر را ندیده. تورنتو همین حالا حدودِ
      // ۹۱۰ رخدادِ زنده دارد — فاصله تا آن سقف یک فصل است، نه یک سال.
      .limit(PAGE_MAX));
    if (error) throw error;
    warnIfFull('eventRepo.hashList', data?.length ?? 0, PAGE_MAX, undefined);
    return (data ?? [])
      .filter((r: any) => !!r.content_hash)
      // ⚠ `image_invalid` عمداً داخلِ هش تا می‌شود و تزئینی نیست: معتبر/غیرمعتبر
      // کردنِ یک عکس `content_hash` را عوض **نمی‌کند** (خودِ ردیف که دست
      // نخورده)، پس بدونِ این، کشِ روی گوشی ردیف را تازه نمی‌کرد و عکسی که
      // همه‌جا پنهان شده روی گوشی از کشِ محلی نشان داده می‌شد.
      .map((r: any) => ({
        id: r.id,
        hash: `${r.content_hash as string}${r.image_invalid ? '|x' : ''}`,
      }));
  },

  /** یه رخدادِ واحد با شناسه - برای وقتی هش عوض شده و باید کاملش رو (با عکس) گرفت. */
  async byIds(ids: string[]): Promise<EventItem[]> {
    if (!ids.length) return [];
    if (isDemoMode) return demoStore.events.upcoming().filter((e) => ids.includes(e.id));
    return inChunks(ids, async (batch) => {
      const { data, error } = await tagged('eventRepo.byIds', supabase
        .from('events')
        .select(`*, venue:venues(name,lat,lng), event_tags(tag_id), event_occurrences!event_occurrences_event_id_fkey(starts_at), ${CITY_JOIN}`)
        .eq('kind', 'event').in('id', batch));
      if (error) throw error;
      return (data ?? []).map(mapEvent);
    });
  },

  /**
   * عمداً بدونِ فیلترِ kind - هم صفحه‌ی جزئیاتِ رخداد ازش استفاده می‌کنه
   * هم صفحه‌ی ویرایش (که باید بتونه نوع رو نشون بده حتی وقتی می‌خوای
   * عوضش کنی، نگاه کن به app/event/edit/[id].tsx).
   */
  async byId(id: string): Promise<EventItem | null> {
    if (isDemoMode) return demoStore.events.byId(id);
    const { data, error } = await tagged('eventRepo.byId', supabase
      .from('events')
      .select(`*, venue:venues(name,lat,lng), event_tags(tag_id), ${CITY_JOIN}`)
      .eq('id', id).maybeSingle());
    if (error) throw error;
    return data ? mapEvent(data) : null;
  },

  /**
   * پیشنهادِ ویرایش برای یه رخدادِ موجود. یه ردیفِ کاملاً جدید با
   * status='pending_review' می‌سازه (مایگریشن 0033)؛ تا ادمین تایید نکنه
   * جای نسخه‌ی فعلی رو نمی‌گیره.
   */
  async proposeEdit(rootId: string, d: EventEditDraft, editNote?: string): Promise<string> {
    if (isDemoMode) return demoStore.events.proposeEdit(rootId, d, editNote);
    const { data, error } = await supabase.rpc('propose_event_edit', {
      p_root_id: rootId,
      p_title: d.title.trim(),
      p_description: d.description?.trim() || null,
      p_category_id: d.tagIds[0] ?? null,
      p_venue_name: d.venueName?.trim() || null,
      p_venue_address: d.venueAddress?.trim() || null,
      p_venue_lat: d.venueLat ?? null,
      p_venue_lng: d.venueLng ?? null,
      p_starts_at: d.startsAt?.toISOString() ?? null,
      p_ends_at: d.endsAt?.toISOString() ?? null,
      p_is_free: d.isFree,
      p_price_note: d.priceNote?.trim() || null,
      p_url: normalizeUrl(d.url),
      p_edit_note: editNote?.trim() || null,
      p_weekdays: d.weekdays?.length ? d.weekdays : null,
      p_kind: d.kind,
      p_has_discount: d.hasDiscount ?? false,
      p_discount_note: d.discountNote?.trim() || null,
      p_address: d.address?.trim() || null,
      p_lat: d.lat ?? null,
      p_lng: d.lng ?? null,
      p_tag_ids: d.tagIds.length ? d.tagIds : null,
      p_monthdays: d.monthdays?.length ? d.monthdays : null,
      p_month_weeks: d.monthWeeks?.length ? d.monthWeeks : null,
      // فهرستِ کاملِ تاریخ‌های پراکنده - نه فقط تازه‌ها. هر ویرایش یه ردیفِ
      // تازه‌ی رخداده و اجراها به ردیفِ قبلی چسبیدن (نگاه کن به مایگریشن 0057).
      p_occurrences: d.occurrences?.length
        ? d.occurrences.map((o) => ({
            starts_at: o.startsAt.toISOString(),
            ends_at: o.endsAt?.toISOString() ?? null,
            title: o.title ?? null,
            url: o.url ?? null,
            venue_id: o.venueId ?? null,
            address: o.address ?? null,
            lat: o.lat ?? null,
            lng: o.lng ?? null,
          }))
        : null,
    });
    if (error) throw error;
    return data as string;
  },

  /** همه‌ی نسخه‌های یه رخداد - فقط ادمین (RLS) چیزی جز نسخه‌ی published می‌بینه. */
  async versions(rootId: string): Promise<EventVersion[]> {
    if (isDemoMode) return demoStore.events.versions(rootId);
    const { data, error } = await tagged('eventRepo.versions', supabase
      .from('events')
      .select(`*, venue:venues(name,lat,lng), event_tags(tag_id), ${CITY_JOIN},`
        + ' editor:profiles!events_edited_by_fkey(display_name),'
        + ' reviewer:profiles!events_reviewed_by_fkey(display_name)')
      .or(`id.eq.${rootId},root_id.eq.${rootId}`));
    if (error) throw error;
    const rows = (data ?? []).map((r: any) => ({
      ...mapEvent(r),
      editedByName: r.editor?.display_name ?? null,
      reviewedByName: r.reviewer?.display_name ?? null,
    }));
    // منتظرِ تایید/رد اول، بعد نسخه‌ی نزولی - صریح، نه با ترتیبِ الفباییِ status.
    return rows.sort((a, b) => {
      const pending = Number(b.status === 'pending_review') - Number(a.status === 'pending_review');
      return pending !== 0 ? pending : (b.version ?? 0) - (a.version ?? 0);
    });
  },

  /**
   * اجراهای یه رخداد - زمان/مکان/لینکِ هر کدوم (مایگریشن 0054).
   *
   * فهرستِ خالی حالتِ عادیه، نه خطا: رخدادی که فقط یه زمان و یه مکان داره
   * اصلاً سطری این‌جا نداره و همون ستون‌های خودش جواب می‌دن.
   */
  async occurrences(eventId: string): Promise<EventOccurrence[]> {
    if (isDemoMode) return demoStore.events.occurrences(eventId);
    const { data, error } = await supabase
      .from('event_occurrences')
      .select('id, starts_at, ends_at, venue_id, address, lat, lng, title, url,'
        + ' venue:venues(name,lat,lng)')
      .eq('event_id', eventId)
      .order('starts_at', { ascending: true, nullsFirst: false });
    if (error) throw error;
    return ((data ?? []) as any[]).map((o) => ({
      id: Number(o.id),
      startsAt: o.starts_at ?? null,
      endsAt: o.ends_at ?? null,
      venueId: o.venue_id ?? null,
      venueName: o.venue?.name ?? null,
      address: o.address ?? null,
      lat: o.venue?.lat ?? o.lat ?? null,
      lng: o.venue?.lng ?? o.lng ?? null,
      title: o.title ?? null,
      url: o.url ?? null,
    }));
  },

  /**
   * صفِ ادمین: هر رخدادی که منتظرِ تاییده - چه تازه از کرالر (eventbrite/
   * allevents/ottawatourism)، چه پیشنهادِ ویرایشِ کاربر، چه رخدادِ پیشنهادیِ
   * تازه. یه فهرستِ واحد چون approve/reject برای هر سه یکیه.
   *
   * از RPC، نه مستقیم از جدول (WHOSUP-311، مایگریشن 0162): ردیف‌هایی که
   * حسابِ داخلی/تستی ساخته یا ویرایش کرده این‌جا نمی‌آیند، و تشخیصش به
   * ایمیل‌ها نیاز دارد که کلاینت نمی‌بیند. عقربه‌ی «صفِ بازبینی» در «سلامت»
   * همین قاعده را دارد. embedها مثلِ قبل روی خروجیِ RPC کار می‌کنند.
   */
  async pendingReview(cityId?: number): Promise<EventItem[]> {
    if (isDemoMode) return [];
    const query = supabase
      .rpc('admin_pending_review', { p_city_id: cityId ?? null })
      .select(`*, venue:venues(name,lat,lng), event_tags(tag_id), event_occurrences!event_occurrences_event_id_fkey(starts_at), ${CITY_JOIN}`);
    const { data, error } = await tagged(
      'eventRepo.pendingReview', query.order('created_at', { ascending: true }),
    );
    if (error) throw error;
    return (data ?? []).map(mapEvent);
  },

  /** فقط شمارشِ نسخه‌ها - برای بجِ ادمین، بدونِ کشیدنِ کل تاریخچه. */
  async versionCount(rootId: string): Promise<number> {
    if (isDemoMode) return demoStore.events.versions(rootId).length;
    const { count, error } = await tagged('eventRepo.versionCount', supabase
      .from('events').select('id', { count: 'exact', head: true })
      .or(`id.eq.${rootId},root_id.eq.${rootId}`));
    if (error) throw error;
    return count ?? 1;
  },

  /** تاییدِ یه نسخه: نسخه‌ی published قبلی superseded میشه، این یکی published. */
  async approve(versionId: string): Promise<void> {
    if (isDemoMode) return demoStore.events.approve(versionId);
    const { error } = await supabase.rpc('approve_event_version', { p_version_id: versionId });
    if (error) throw error;
  },

  /** ردِ یه نسخه‌ی pending، یا حذفِ نسخه‌ی لایو - هر دو با همین. دلیل اجباریه. */
  async reject(versionId: string, reason: string): Promise<void> {
    if (isDemoMode) return demoStore.events.reject(versionId, reason);
    const { error } = await supabase.rpc('reject_event_version', {
      p_version_id: versionId, p_reason: reason,
    });
    if (error) throw error;
  },

  /**
   * پیشنهاد کاربر. با وضعیت pending_review ثبت میشه و تا تایید دیده نمیشه.
   *
   * `cityId` همون شهریه که کاربر موقعِ پیشنهاد دادن داشته می‌دیده — پیشنهادِ
   * بی‌شهر از مایگریشن 0063 به بعد اصلاً نوشته نمی‌شه (ستون not null است).
   */
  async suggest(uid: UserId, cityId: number, d: SuggestionDraft): Promise<void> {
    if (isDemoMode) return demoStore.events.suggest(uid, cityId, d);
    const { data, error } = await tagged('eventRepo.suggest', supabase.from('events').insert({
      kind: 'event',
      source: 'user_submitted',
      city_id: cityId,
      title: d.name.trim(),
      description: d.description.trim() || null,
      category_id: d.tagIds[0] ?? null,
      url: normalizeUrl(d.url),
      // لینکِ بلیط جداست از لینکِ معرفی (مایگریشن 0077).
      ticket_url: normalizeUrl(d.ticketUrl) || null,
      image_url: d.imageUrl || null,
      is_organizer: d.isOrganizer ?? false,
      // مکان روی خودِ ردیف می‌شینه، نه توی `venues` — دلیلش توی مایگریشن
      // 0079 نوشته شده. `itemFields` هم از قبل همین ترتیب رو دنبال می‌کنه.
      address: d.address.trim() || null,
      lat: d.lat ?? null,
      lng: d.lng ?? null,
      starts_at: d.startsAt?.toISOString() ?? null,
      ends_at: d.endsAt?.toISOString() ?? null,
      // الگوی تکرار (مایگریشن‌های 0038 و 0057) - «هر پنجشنبه»، «اولین جمعه‌ی
      // هر ماه»، «روزِ ۱ هر ماه».
      weekdays: d.weekdays?.length ? d.weekdays : null,
      monthdays: d.monthdays?.length ? d.monthdays : null,
      month_weeks: d.monthWeeks?.length ? d.monthWeeks : null,
      is_free: d.isFree,
      status: 'pending_review',
      submitted_by: uid,
    }).select('id').single());
    if (error) throw error;
    if (d.tagIds.length) {
      const { error: tagError } = await supabase.from('event_tags')
        .insert(d.tagIds.map((tag_id) => ({ event_id: data.id, tag_id })));
      if (tagError) throw tagError;
    }
    // تاریخ‌های اضافه. اولی روی خودِ ردیف نشسته؛ این‌ها بقیه‌شونن
    // (مایگریشن 0054، و سیاستِ نوشتنش توی 0058).
    if (d.occurrences?.length) {
      const { error: occError } = await supabase.from('event_occurrences')
        .insert(d.occurrences.map((o) => ({
          event_id: data.id,
          starts_at: o.startsAt.toISOString(),
          ends_at: o.endsAt?.toISOString() ?? null,
        })));
      if (occError) throw occError;
    }
  },

  /**
   * رخدادهایی که **خودِ این کاربر** فرستاده — خوراکِ پینِ بالای صفحه‌ی کشف.
   *
   * همه‌ی نسخه‌ها برمی‌گردن، نه فقط نماینده‌ی هر کدوم: تصمیمِ «کدوم نسخه رو
   * نشون بده و برچسبش چیه» توی `lib/myEvents.ts` گرفته می‌شه تا گوشی و سایت
   * دو تا جوابِ کمی متفاوت ندن.
   *
   * فیلترِ تاریخ عمداً این‌جا نیست: `pinnedEvents` خودش با «الان»ِ همون رندر
   * می‌بُرَدش. یه فیلترِ سرور یعنی رخدادی که وسطِ باز بودنِ صفحه تموم می‌شه
   * تا رفرشِ بعدی پین می‌مونه.
   *
   * سیاستِ `e_sel` (مایگریشن 0012) همینو اجازه می‌ده و نه بیشتر: هر ردیفی
   * که `submitted_by`ش خودتی، در هر وضعیتی.
   */
  async mine(uid: UserId, cityId: number): Promise<EventItem[]> {
    if (isDemoMode) return demoStore.events.mine();
    const { data, error } = await tagged('eventRepo.mine', supabase
      .from('events')
      .select(`*, venue:venues(name,lat,lng), event_tags(tag_id), ${CITY_JOIN}`)
      // ⚠ ترتیبِ فیلترها این‌جا تزئینی است ولی ایندکسش نه: تا مایگریشن 0143
      // هیچ ایندکسی `submitted_by` نداشت و این کوئری کلِ جدول را می‌خواند تا
      // جوابِ «هیچی» بدهد — همان نیمی از سطرهای `rows:0`ِ لاگ در WHOSUP-209.
      // حالا روی `events_submitted_by_idx` می‌نشیند.
      .eq('kind', 'event').eq('city_id', cityId).eq('submitted_by', uid)
      .order('ends_at', { ascending: true, nullsFirst: false })
      .limit(200));
    if (error) throw error;
    return (data ?? []).map(mapEvent);
  },

  /**
   * ذخیره‌ی ویرایشِ رخدادی که خودت فرستادی (مایگریشن 0077).
   *
   * برخلافِ `proposeEdit` که همیشه یه نسخه‌ی منتظرِ تایید می‌سازه، این تابع
   * می‌تونه **مستقیم** روی همون ردیف بنویسه — بسته به اینکه فرستنده خودش رو
   * برگزارکننده معرفی کرده یا فقط خبررسان. کدوم اتفاق افتاده رو خودِ جواب
   * می‌گه (`needsReview`)، چون پیامِ بعدش فرق می‌کنه.
   */
  async saveOwn(
    eventId: string, d: EventEditDraft, editNote?: string,
  ): Promise<{ id: string; needsReview: boolean }> {
    if (isDemoMode) return demoStore.events.saveOwn(eventId, d, editNote);
    const { data, error } = await supabase.rpc('save_own_event', {
      p_event_id: eventId,
      p_title: d.title.trim(),
      p_description: d.description?.trim() || null,
      // مکانِ رخدادِ کاربر روی خودِ ردیفه (مایگریشن 0079)، پس `address` و
      // مختصات می‌رن — نه نامِ venue.
      p_address: d.address?.trim() || null,
      p_lat: d.lat ?? null,
      p_lng: d.lng ?? null,
      p_starts_at: d.startsAt?.toISOString() ?? null,
      p_ends_at: d.endsAt?.toISOString() ?? null,
      p_is_free: d.isFree,
      p_price_note: d.priceNote?.trim() || null,
      p_url: normalizeUrl(d.url),
      p_ticket_url: normalizeUrl(d.ticketUrl) || null,
      p_image_url: d.imageUrl || null,
      p_tag_ids: d.tagIds.length ? d.tagIds : null,
      p_weekdays: d.weekdays?.length ? d.weekdays : null,
      p_monthdays: d.monthdays?.length ? d.monthdays : null,
      p_month_weeks: d.monthWeeks?.length ? d.monthWeeks : null,
      // همون قرارِ proposeEdit: فهرستِ **کامل** می‌ره، چون تابع هر بار از نو
      // می‌نویسدشون و هرچی نره یعنی حذف شده.
      p_occurrences: d.occurrences?.length
        ? d.occurrences.map((o) => ({
            starts_at: o.startsAt.toISOString(),
            ends_at: o.endsAt?.toISOString() ?? null,
            title: o.title ?? null,
            url: o.url ?? null,
          }))
        : null,
      p_edit_note: editNote?.trim() || null,
    });
    if (error) throw error;
    const row = (data ?? {}) as { id: string; needs_review: boolean };
    return { id: row.id, needsReview: !!row.needs_review };
  },

  /**
   * پس گرفتنِ رخدادِ خودت (مایگریشن 0078).
   *
   * کلِ زنجیره‌ی نسخه‌ها با هم `withdrawn` می‌شه: از پینِ خودت، از فهرستِ
   * عمومی و از صفِ بازبینی هر سه بیرون می‌ره. ردیف پاک نمی‌شه — دلیلش توی
   * خودِ مایگریشن نوشته شده.
   */
  async withdrawOwn(eventId: string): Promise<void> {
    if (isDemoMode) return demoStore.events.withdrawOwn(eventId);
    const { error } = await supabase.rpc('withdraw_own_event', { p_event_id: eventId });
    if (error) throw error;
  },

  /**
   * عکسِ کاورِ رخداد → باکتِ `event-images` (مایگریشن 0077)، و آدرسِ عمومیش
   * برمی‌گرده.
   *
   * مو به موی `profileRepo.uploadAvatar`: پوشه‌ی به‌نامِ خودِ کاربر (که سیاستِ
   * باکت روش سواره) و اسمِ یکتا، چون آدرسِ عمومی کش می‌شه و اسمِ ثابت یعنی
   * عکسِ قبلی می‌مونه.
   */
  async uploadImage(uid: UserId, base64: string): Promise<string> {
    if (isDemoMode) return demoStore.events.uploadImage(base64);
    const path = `${uid}/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.jpg`;
    const body = base64ToArrayBuffer(base64);
    const { error } = await supabase.storage
      .from(EVENT_IMAGE_BUCKET)
      .upload(path, body, { contentType: 'image/jpeg', upsert: false });
    if (error) {
      // اندازه هم لاگ می‌شه: بیشترِ شکست‌های آپلود سرِ سقفِ حجمِ باکته.
      reportError('eventRepo.uploadImage', error, { bytes: body.byteLength, path });
      throw error;
    }
    return supabase.storage.from(EVENT_IMAGE_BUCKET).getPublicUrl(path).data.publicUrl;
  },

  /**
   * عکسِ جایگزین‌شده رو پاک می‌کنه تا باکت زباله جمع نکنه. خطاش مهم نیست.
   *
   * ورودی همون آدرسِ عمومیه که توی ستون نشسته، پس مسیرِ واقعی از توش در
   * می‌آد. آدرسی که مالِ این باکت نیست (عکسِ کرالر) بی‌صدا رد می‌شه — پاک
   * کردنِ چیزی که مالِ ما نیست بدترین کاریه که می‌شد این‌جا کرد.
   */
  async removeImage(url?: string | null): Promise<void> {
    if (isDemoMode || !url) return;
    const marker = `/${EVENT_IMAGE_BUCKET}/`;
    const at = url.indexOf(marker);
    if (at < 0) return;
    const path = url.slice(at + marker.length);
    const { error } = await supabase.storage.from(EVENT_IMAGE_BUCKET).remove([path]);
    if (error) reportError('eventRepo.removeImage', error, { path });
  },
};

/**
 * بیزینس همون جدولِ events است، kind='business' (مایگریشن 0042) - جدولِ
 * جدای businesses دیگه وجود نداره (به businesses_deprecated_0042 تغییرِ
 * اسم داده شد، فقط برای برگشت‌پذیری، هیچ‌جا ازش خونده نمی‌شه).
 */
const mapBusiness = (b: any): Business => ({
  id: b.id, shortId: b.short_id ?? undefined,
  name: b.title, description: b.description, categoryId: b.category_id,
  tagIds: (b.event_tags ?? []).map((t: any) => t.tag_id),
  address: b.address, lat: b.lat, lng: b.lng, url: b.url,
  city: b.city_ref?.name ?? null,
  cityId: b.city_id == null ? null : Number(b.city_id),
  // همان قاعده‌ی mapEvent — عکسِ غیرمعتبر پنهان می‌شود، نه پاک (مایگریشن 0135).
  imageUrl: primaryImageUrl(b),
  contentHash: b.content_hash ?? null, createdAt: b.created_at ?? null,
  hasDiscount: b.has_discount, discountNote: b.discount_note,
  isPromoted: b.is_promoted, status: b.status,
});

export const businessRepo = {
  /**
   * پارامترِ شهر از اول این‌جا بود ولی **هیچ‌وقت استفاده نمی‌شد** — فهرستِ
   * مکان‌ها بی‌سروصدا مالِ کلِ دیتابیس بود، نه شهرِ جاری. تا وقتی یه شهر
   * بیشتر نبود کسی نمی‌فهمید؛ از این به بعد می‌فهمید.
   */
  async list(cityId: number, opts: { limit?: number; offset?: number } = {}): Promise<Business[]> {
    if (isDemoMode) return demoStore.businesses.list();
    // بی‌فیلترِ تاریخ، برخلافِ رخداد: مکان نه شروع داره نه پایان — یه کافه
    // «تمام» نمی‌شه. نگاه کن به stillOnFilter و به قاعده‌ی همین‌شکلیِ
    // matchesWhen که مکان‌ها رو دست‌نخورده رد می‌کنه.
    const { limit = PAGE_MAX, offset = 0 } = opts;
    const { data, error } = await tagged('businessRepo.list', supabase
      .from('events')
      .select(`*, event_tags(tag_id), ${CITY_JOIN}`)
      .eq('kind', 'business').eq('city_id', cityId).eq('status', 'published')
      .order('is_promoted', { ascending: false })
      .order('title')
      .range(offset, offset + limit - 1));
    if (error) throw error;
    warnIfFull('businessRepo.list', data?.length ?? 0, limit, opts.limit);
    return (data ?? []).map(mapBusiness);
  },

  /** شمارشِ واقعیِ بیزینس‌های منتشرشده - از دیتابیس، نه از طولِ چیزی که لود شده. */
  async listCount(cityId: number): Promise<number> {
    if (isDemoMode) return demoStore.businesses.list().length;
    const { count, error } = await tagged('businessRepo.listCount', supabase
      .from('events').select('id', { count: 'exact', head: true })
      .eq('kind', 'business').eq('city_id', cityId).eq('status', 'published'));
    if (error) throw error;
    return count ?? 0;
  },

  /**
   * فقط شناسه+هش - همون منطقِ eventRepo.hashList برای کشِ رویِ گوشی، از جمله
   * ترتیبِ صریح: این‌جا هم صفحه‌بندیِ کش روی همین ترتیب سواره و بی‌ترتیبْ
   * هر صفحه یه مشتِ تصادفیه. ترتیبْ همون `businessRepo.list` است.
   */
  async hashList(cityId: number): Promise<HashEntry[]> {
    if (isDemoMode) {
      return demoStore.businesses.list().map((b) => ({ id: b.id, hash: b.contentHash ?? b.id }));
    }
    const { data, error } = await tagged('businessRepo.hashList', supabase
      .from('events').select('id, content_hash')
      .eq('kind', 'business').eq('city_id', cityId).eq('status', 'published')
      .order('is_promoted', { ascending: false })
      .order('title')
      // همون دلیلِ `eventRepo.hashList`: بدونِ سقفِ صریح، سقفِ نامرئیِ هزار
      // بی‌صدا می‌بُرَد و کشِ گوشی خبردار نمی‌شود.
      .limit(PAGE_MAX));
    if (error) throw error;
    warnIfFull('businessRepo.hashList', data?.length ?? 0, PAGE_MAX, undefined);
    return (data ?? [])
      .filter((r: any) => !!r.content_hash)
      .map((r: any) => ({ id: r.id, hash: r.content_hash as string }));
  },

  /** چند بیزینس با شناسه - برای وقتی هش عوض شده. */
  async byIds(ids: string[]): Promise<Business[]> {
    if (!ids.length) return [];
    if (isDemoMode) return demoStore.businesses.list().filter((b) => ids.includes(b.id));
    return inChunks(ids, async (batch) => {
      const { data, error } = await tagged('businessRepo.byIds', supabase
        .from('events').select(`*, event_tags(tag_id), ${CITY_JOIN}`)
        .eq('kind', 'business').in('id', batch));
      if (error) throw error;
      return (data ?? []).map(mapBusiness);
    });
  },

  async byId(id: string): Promise<Business | null> {
    if (isDemoMode) return demoStore.businesses.byId(id);
    const { data, error } = await tagged('businessRepo.byId', supabase
      .from('events').select(`*, event_tags(tag_id), ${CITY_JOIN}`)
      .eq('kind', 'business').eq('id', id).maybeSingle());
    if (error) throw error;
    return data ? mapBusiness(data) : null;
  },

  /** همون قاعده‌ی eventRepo.suggest: شهر اجباریه. */
  async suggest(uid: UserId, cityId: number, d: SuggestionDraft): Promise<void> {
    if (isDemoMode) return demoStore.businesses.suggest();
    const { data, error } = await tagged('businessRepo.suggest', supabase.from('events').insert({
      kind: 'business',
      source: 'user_submitted',
      city_id: cityId,
      title: d.name.trim(),
      description: d.description.trim() || null,
      category_id: d.tagIds[0] ?? null,
      address: d.address.trim() || null,
      // نقطه‌ی روی نقشه — همون فیلدی که رخداد داره. مکان بیشتر از رخداد
      // بهش احتیاج داره: یه کافه‌ی توی کوچه با آدرسِ تنها پیدا نمی‌شه.
      lat: d.lat ?? null,
      lng: d.lng ?? null,
      url: normalizeUrl(d.url),
      image_url: d.imageUrl || null,
      has_discount: d.hasDiscount,
      discount_note: d.hasDiscount ? d.discountNote.trim() || null : null,
      status: 'pending_review',
      submitted_by: uid,
    }).select('id').single());
    if (error) throw error;
    if (d.tagIds.length) {
      const { error: tagError } = await supabase.from('event_tags')
        .insert(d.tagIds.map((tag_id) => ({ event_id: data.id, tag_id })));
      if (tagError) throw tagError;
    }
  },
};

export const availabilityRepo = {
  async mine(uid: UserId): Promise<AvailabilityCell[]> {
    if (isDemoMode) return demoStore.availability.mine();
    const { data, error } = await supabase
      .from('availability_slots').select('*').eq('user_id', uid);
    if (error) throw error;
    return (data ?? []).map((r: any) => ({ dow: r.dow as Dow, hour: r.hour, state: r.state }));
  },

  async setCells(uid: UserId, cells: { dow: number; hour: number }[], on: boolean) {
    if (isDemoMode) return demoStore.availability.setCells(cells, on);
    if (!cells.length) return;
    if (on) {
      const { error } = await supabase.from('availability_slots').upsert(
        cells.map((c) => ({ user_id: uid, dow: c.dow, hour: c.hour, state: 'free' })),
      );
      if (error) throw error;
    } else {
      for (const c of cells) {
        await supabase.from('availability_slots')
          .delete().eq('user_id', uid).eq('dow', c.dow).eq('hour', c.hour);
      }
    }
  },

  async commonSlots(userIds: UserId[]): Promise<RankedSlot[]> {
    if (isDemoMode) return demoStore.availability.commonSlots();
    const { data, error } = await supabase.rpc('common_free_slots', { p_user_ids: userIds });
    if (error) throw error;
    return (data ?? []).map((r: any) => ({ dow: r.dow, hour: r.hour, freeCount: Number(r.free_count) }));
  },
};

/** سطرِ خام (snake_case) → شکلِ اپ. هم فهرست ازش می‌سازد، هم کانالِ زنده. */
const toNotification = (n: any): AppNotification => ({
  id: n.id, kind: n.kind, title: n.title, body: n.body,
  titleKey: n.title_key ?? null, titleVars: n.title_vars ?? null,
  bodyKey: n.body_key ?? null, bodyVars: n.body_vars ?? null,
  data: n.data ?? {}, readAt: n.read_at, sentAt: n.sent_at,
  createdAt: n.scheduled_for,
});

export const notificationRepo = {
  /**
   * `limit` از وقتی پارامتر شد که صفحه‌ی «همه‌ی اعلان‌ها» آمد (WHOSUP-131):
   * اون صفحه با هر «موارد بیشتر» سقفِ خودش رو بالا می‌بره، ولی صداکننده‌های
   * قدیمی (اعلانِ داخلِ اپ) همون ۴۰ تای همیشگی رو می‌گیرن.
   */
  async list(uid: UserId, opts: { limit?: number } = {}): Promise<AppNotification[]> {
    if (isDemoMode) return demoStore.notifications.list().slice(0, opts.limit ?? 40);
    const { data, error } = await supabase
      .from('notifications').select('*').eq('user_id', uid)
      .order('scheduled_for', { ascending: false }).limit(opts.limit ?? 40);
    if (error) throw error;
    return (data ?? []).map(toNotification);
  },
  /*
   * `remove` این‌جا بود و برداشته شد.
   *
   * تنها استفاده‌کننده‌ش صفحه‌ی فهرستِ اعلان‌ها بود (کشیدنِ ردیف = حذف)، و
   * اون صفحه با رفتنِ زنگوله حذف شد. نگه داشتنش بی‌مصرف نبود، خطرناک بود:
   * حذفِ واقعیِ ردیف، قاعده‌ی «هر دوستِ پذیرفته‌شده باید اعلانِ این سیگنال
   * رو داشته باشه» رو برای همیشه می‌شکست (scripts/check-invariants.mjs) و
   * جلوی انتشار رو می‌گرفت — بدون اینکه هیچ باگی در کار باشه.
   */

  /**
   * یه اعلانِ مشخص رو خونده می‌کنه.
   *
   * برای اعلان‌های اقدام‌خواهی لازمه که کارشون «فهمیدم» است و نه چیزِ
   * دیگه‌ای — یعنی `friend_declined` (مایگریشن 0075). بقیه‌شون با خودِ کار
   * (جواب دادن به سیگنال، قبول/رد کردنِ درخواست) و از سمتِ دیتابیس پاک می‌شن.
   */
  async markRead(id: number) {
    if (isDemoMode) return demoStore.notifications.markRead(id);
    const { error } = await supabase.from('notifications')
      .update({ read_at: new Date().toISOString() }).eq('id', id);
    if (error) throw error;
  },

  async unreadCount(uid: UserId): Promise<number> {
    if (isDemoMode) return demoStore.notifications.unreadCount();
    const { count } = await supabase
      .from('notifications').select('id', { count: 'exact', head: true })
      .eq('user_id', uid).is('read_at', null);
    return count ?? 0;
  },

  /**
   * نخوانده‌ها به تفکیک دسته، تا هر دسته طبق تنظیم خودش نشون داده شه.
   * نگاشتِ kind→category عمداً آینه‌ی تابع `notification_category` توی 0008 است؛
   * اگه اون‌جا دسته‌ای اضافه شد، این‌جا هم باید اضافه شه.
   */
  async unreadByCategory(uid: UserId): Promise<Record<NotifCategory, number>> {
    if (isDemoMode) return demoStore.notifications.unreadByCategory();
    const { data, error } = await supabase
      .from('notifications').select('kind').eq('user_id', uid).is('read_at', null);
    if (error) throw error;

    const out: Record<NotifCategory, number> = { friend_request: 0, signal: 0, event: 0 };
    for (const row of (data ?? []) as { kind: string }[]) {
      out[categoryOfKind(row.kind)] += 1;
    }
    return out;
  },
  async markAllRead(uid: UserId) {
    if (isDemoMode) return demoStore.notifications.markAllRead();
    await supabase.from('notifications')
      .update({ read_at: new Date().toISOString() })
      .eq('user_id', uid).is('read_at', null);
  },

  /**
   * فقط اعلان‌های خبریِ **یک صفحه** رو «دیده شد» می‌کنه.
   *
   * اعلان‌های اقدام‌خواه (دعوت به سیگنال، درخواست دوستی) عمداً دست‌نخورده می‌مونن:
   * نشانه باید فهرستِ کارهای مونده باشه نه تاریخچه، و صرفِ دیدنِ صفحه یعنی
   * «دیدم»، نه «انجام دادم». اون‌ها با خودِ جواب دادن پاک می‌شن (مایگریشن 0010).
   *
   * `scope` از وقتی لازم شد که زنگوله رفت و نقطه‌ها روی تب‌ها تنها نشانه شدن:
   * قبلاً باز کردنِ صفحه‌ی اول همه‌ی دسته‌ها رو خوانده می‌کرد، یعنی نقطه‌ی
   * «فلانی به اپ اومد» روی تبِ دوستان قبل از دیده شدن غیب می‌شد. حالا هر
   * صفحه فقط خبرِ خودش رو می‌خونه.
   */
  async markSeen(uid: UserId, scope: 'friend' | 'rest') {
    if (isDemoMode) return demoStore.notifications.markSeen(scope);
    const q = supabase.from('notifications')
      .update({ read_at: new Date().toISOString() })
      .eq('user_id', uid)
      // خبرِ گفتگو/عکس هم این‌جا نیست: «پیامی هست که ندیده‌ای» با نگاه
      // کردن به **فهرستِ برنامه‌ها** دیده نشده — با باز کردنِ خودِ آن سربرگ
      // دیده می‌شه (`markSignalTabSeen` پایین). همون استثنای الارم، فقط
      // این‌جا نوعِ خبر تعیینش می‌کنه نه گیرنده‌ش.
      .not('kind', 'in', `(${[...ACTIONABLE_KINDS, ...TAB_SEEN_KINDS].join(',')})`)
      // الارم هم مثلِ اعلان‌های اقدام‌خواه با نگاه کردن پاک نمی‌شه، ولی
      // برخلافِ اون‌ها نوعش این رو تعیین نمی‌کنه بلکه گیرنده‌ش: همون
      // «عوض شد» برای کسی که گفته هستم الارمه و برای بقیه خبر. با جواب
      // دادنِ دوباره پاک می‌شه (تریگرِ 0010/0072).
      //
      // ⚠️ `or` و نه یه `not(...eq...)`ِ ساده: اعلان‌های قدیمی اصلاً کلیدِ
      // `alarm` ندارن، و `data->>'alarm'` براشون NULL است. توی SQL
      // `NOT (NULL = 'true')` می‌شه NULL، یعنی ردیف **رد می‌شه** — که یعنی
      // هیچ اعلانِ عادی‌ای دیگه «دیده شد» نمی‌شد و نقطه‌ها هیچ‌وقت پاک
      // نمی‌شدن.
      .or('data->>alarm.is.null,data->>alarm.neq.true')
      .is('read_at', null);
    await (scope === 'friend'
      ? q.in('kind', FRIEND_KINDS)
      : q.not('kind', 'in', `(${FRIEND_KINDS.join(',')})`));
  },

  /**
   * «گفتگو/عکس‌های این برنامه را دیدم» (مایگریشن 0148).
   *
   * برخلافِ `markSeen` که با باز شدنِ یک **صفحه** همه‌ی خبرها را می‌خواند،
   * این یکی فقط خبرِ همان سربرگ و همان برنامه را می‌خواند — چون تنها همان
   * واقعاً دیده شده.
   *
   * ⚠ صداکردنش اختیاری نیست: تریگرِ 0148 تا وقتی یک سطرِ نخوانده هست سطرِ
   * تازه نمی‌سازد، پس صفحه‌ای که این را صدا نزند، اولین اعلان را تا ابد
   * نگه می‌دارد و پیامِ دوم به بعد هیچ خبری نمی‌دهد.
   */
  async markSignalTabSeen(uid: UserId, signalId: string, tab: 'chat' | 'photos') {
    if (isDemoMode) return demoStore.notifications.markSignalTabSeen(tab);
    await supabase.from('notifications')
      .update({ read_at: new Date().toISOString() })
      .eq('user_id', uid)
      .eq('kind', SIGNAL_TAB_KIND[tab])
      .contains('data', { signal_id: signalId })
      .is('read_at', null);
  },

  /**
   * لایه‌ی اولِ باخبر شدن: هر ردیفِ تازه‌ای که برای **من** درج شود، همان
   * لحظه. الگو عیناً `signalRepo.subscribeMessages` است.
   *
   * تا وقتی این کانال زنده است، نظرسنجی (`core/lib/notifPolling.ts`) فقط
   * توری ایمنی است و می‌تواند دقیقه‌ای باشد نه ثانیه‌ای — همان چیزی که
   * فاصله‌ی ۲۰ ثانیه‌ای را در هر دو اپ غیرلازم کرد.
   *
   * فیلترِ `user_id` صرفاً برای کم کردنِ ترافیک نیست: RLS هم همین را
   * می‌گوید، پس بدونش کانال چیزی جز ردیف‌های خودت نمی‌گیرد و فقط بی‌هوده
   * باز می‌ماند.
   */
  /**
   * سطرِ تازه به کال‌بک هم داده می‌شود (برای اعلانکِ داخلِ صفحه در وب —
   * WHOSUP-71)؛ صداکننده‌ای که فقط «خبری شد» می‌خواهد، مثلِ شمارنده‌ها،
   * آرگومان را نمی‌گیرد و همان‌طور که بود کار می‌کند.
   */
  subscribeNew(uid: UserId, onNew: (fresh?: AppNotification) => void): () => void {
    if (isDemoMode) return () => undefined;
    // نامِ کانال ثابت است (یکی به‌ازای هر کاربر) و `router.replace('/(tabs)')`
    // این لایه را دوباره سوار می‌کند؛ بدونِ این، اشتراکِ تازه روی کانالِ
    // مشترک‌شده‌ی قبلی می‌نشست و **وسطِ رندر** خطا می‌داد — یعنی صفحه‌ی
    // سفیدِ قفل. نگاه کن به lib/realtimeChannel.ts.
    const ch = freshChannel(supabase, `notif-${uid}`)
      .on('postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'notifications', filter: `user_id=eq.${uid}` },
        (payload: { new?: unknown }) => {
          onNew(payload?.new ? toNotification(payload.new) : undefined);
        })
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  },
};
